import { parseDeltaValue } from '../../lib/quillDelta'
import type { PlanHuntWizardStep } from './PlanHuntWizardShell'

/**
 * The plan-hunt rail's check mark means "this section holds something", never
 * "the cursor has been here". Deriving it from the step index instead — the
 * shape this replaces — painted a green check on every step behind the cursor,
 * so a user who clicked straight through the rail was told six empty sections
 * were complete.
 *
 * Only Basics carries required data (a team and a valid start). The other five
 * steps are optional by design — a hunt sends with just those two — so an empty
 * one reads as plain and un-started ('pending'), never as a blocker ('locked').
 */
export type PlanHuntStepId = 'basics' | 'leaders' | 'participants' | 'invitation' | 'drives' | 'game'

/** Whether each step's own section actually holds something the user put there. */
export interface PlanHuntStepFilled {
  /** Basics is the only required step: its team AND start must both be valid. */
  basics: boolean
  leaders: boolean
  participants: boolean
  invitation: boolean
  drives: boolean
  game: boolean
}

export function planHuntStepState(
  id: PlanHuntStepId,
  index: number,
  currentStepIndex: number,
  filled: PlanHuntStepFilled,
): PlanHuntWizardStep['state'] {
  if (index === currentStepIndex) return 'active'
  if (filled[id]) return 'done'
  return id === 'basics' ? 'locked' : 'pending'
}

/**
 * The invitation step counts as filled when the hunter receives *anything* the
 * user authored — message text, an embedded image, or an attachment.
 *
 * The message is a Quill Delta whose "empty" document is a single newline
 * insert, so a plain emptiness check on the raw string would call an untouched
 * editor filled. Embeds (`insert` is an object, e.g. an image) are content even
 * though they carry no text.
 */
export function planHuntInvitationFilled(
  message: string | null,
  attachmentDocumentIds: readonly unknown[],
  attachmentFiles: readonly unknown[],
): boolean {
  if (attachmentDocumentIds.length > 0 || attachmentFiles.length > 0) return true
  const parsed = parseDeltaValue(message)
  switch (parsed.kind) {
    case 'empty':
      return false
    case 'plain':
      return parsed.text.trim().length > 0
    case 'delta':
      return parsed.ops.some((op) => {
        const insert = (op as { insert?: unknown }).insert
        return typeof insert === 'string' ? insert.trim().length > 0 : insert != null
      })
  }
}
